import wordmarkOrangeSvgUrl from '@/assets/wntd-wordmark-orange.svg';
import { Capacitor } from '@capacitor/core';
import SaveToGallery from '@/plugins/SaveToGallery';

export async function downloadBrandedImage(
  imageUrl: string,
  filename: string,
  label?: string,
): Promise<void> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // Load the source image
  const img = await loadImage(imageUrl);

  const footerHeight = Math.round(img.width * 0.07);
  const padding = Math.round(footerHeight * 0.3);

  canvas.width = img.width;
  canvas.height = img.height + footerHeight;

  // Draw the original image
  ctx.drawImage(img, 0, 0);

  // Draw orange wordmark watermark in bottom-right corner (no rotation)
  const watermark = await loadImage(wordmarkOrangeSvgUrl);
  const stickerWidth = img.width * 0.25;
  const stickerHeight = (watermark.height / watermark.width) * stickerWidth;

  const margin = img.width * 0.04;
  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.drawImage(
    watermark,
    img.width - stickerWidth - margin,
    img.height - stickerHeight - margin,
    stickerWidth,
    stickerHeight,
  );
  ctx.restore();

  // Draw footer background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, img.height, img.width, footerHeight);

  // Footer text
  const fontSize = Math.round(footerHeight * 0.3);
  ctx.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.textBaseline = 'middle';
  const centerY = img.height + footerHeight / 2;

  // Left: context label (e.g. "Made with Try On by WNTD")
  ctx.textAlign = 'left';
  ctx.fillText(label ?? 'Made with WNTD', padding, centerY);

  // Right: domain
  ctx.textAlign = 'right';
  ctx.fillText('wntd.ai', img.width - padding, centerY);

  // Save the image
  if (Capacitor.isNativePlatform()) {
    // Native: save to photo library via Capacitor plugin
    const base64 = canvas.toDataURL('image/png').split(',')[1];
    await SaveToGallery.saveImage({ base64, filename });
  } else {
    // Web: browser download fallback
    const blob = await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b!), 'image/png')
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
